import { clsx } from 'clsx'
import * as SelectPrimitive from '@radix-ui/react-select'
import { ChevronDown, Check } from 'lucide-react'

// ── BigButton (large tap target for tablet) ──────────────
export function BigButton({ variant = 'primary', onClick, disabled, children, className }) {
  const variants = {
    primary:   'big-btn-primary',
    success:   'big-btn-success',
    secondary: 'big-btn-secondary',
    warning:   'big-btn-warning',
    danger:    'big-btn-danger',
  }
  return (
    <button
      disabled={disabled}
      onClick={onClick}
      className={clsx(
        'flex items-center justify-center text-center transition-all active:scale-95',
        variants[variant] || variants.primary, 
        disabled && 'opacity-40 cursor-not-allowed', 
        className
      )}
    >
      {children}
    </button>
  )
}

// ── Admin Button ─────────────────────────────────────────
export function Button({ variant = 'primary', size, onClick, disabled, children, className, type = 'button' }) {
  return (
    <button
      type={type}
      disabled={disabled}
      onClick={onClick}
      className={clsx(
        'flex items-center justify-center text-center font-bold transition-all active:scale-95',
        variant === 'primary'   && 'btn-primary',
        variant === 'secondary' && 'btn-secondary',
        variant === 'danger'    && 'btn-danger',
        size === 'sm'           && 'btn-sm',
        disabled                && 'opacity-50 cursor-not-allowed',
        className
      )}
    >
      {children}
    </button>
  )
}

// ── Badge ────────────────────────────────────────────────
const BADGE_VARIANTS = { blue: 'badge-blue', teal: 'badge-teal', green: 'badge-green', amber: 'badge-amber', red: 'badge-red', gray: 'badge-gray', navy: 'badge-navy' }
export function Badge({ variant = 'gray', children }) {
  return <span className={BADGE_VARIANTS[variant] || 'badge-gray'}>{children}</span>
}

// ── StepBar ──────────────────────────────────────────────
export function StepBar({ steps, current }) {
  return (
    <div className="step-bar">
      {steps.map((label, i) => {
        const state = i < current ? 'done' : i === current ? 'active' : 'pending'
        return (
          <div key={i} className={`step step-${state}`}>
            <div className="step-circle">{state === 'done' ? '✓' : i + 1}</div>
            <span className="step-label">{label}</span>
            {i < steps.length - 1 && <div className="step-line" />}
          </div>
        )
      })}
    </div>
  )
}

// ── InfoCard ─────────────────────────────────────────────
export function InfoCard({ rows = [], className, style }) {
  return (
    <div className={clsx('info-card', className)} style={style}>
      {rows.map(({ label, value, valueVariant }, i) => (
        <div key={i} className="info-row">
          <span className="info-label">{label}</span>
          <span className={clsx('info-value', valueVariant && `info-value-${valueVariant}`)}>{value}</span>
        </div>
      ))}
    </div>
  )
}

// ── FlowNote ─────────────────────────────────────────────
export function FlowNote({ children, variant = 'brand' }) {
  return (
    <div className={variant === 'teal' ? 'flow-note-teal' : variant === 'amber' ? 'flow-note-amber' : 'flow-note'}>
      {children}
    </div>
  )
}

// ── ScanZone ─────────────────────────────────────────────
const QR_ICON = (color) => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
    <rect x="3" y="3" width="7" height="7" rx="1" stroke={color} strokeWidth="2"/>
    <rect x="14" y="3" width="7" height="7" rx="1" stroke={color} strokeWidth="2"/>
    <rect x="3" y="14" width="7" height="7" rx="1" stroke={color} strokeWidth="2"/>
    <rect x="14" y="14" width="3" height="3" fill={color}/>
    <rect x="19" y="14" width="2" height="2" fill={color}/>
    <rect x="14" y="19" width="2" height="2" fill={color}/>
    <rect x="18" y="18" width="3" height="3" fill={color}/>
  </svg>
)
export function ScanZone({ title, subtitle, teal = false }) {
  const color = teal ? '#0D7A5F' : '#1D6FA8'
  return (
    <div className={teal ? 'scan-zone-teal' : 'scan-zone'}>
      <div className={clsx('w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-3', teal ? 'bg-teal-100' : 'bg-brand-100')}>
        {QR_ICON(color)}
      </div>
      <p className="text-base font-medium text-gray-800 mb-1">{title}</p>
      {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
    </div>
  )
}

// ── QRBox ────────────────────────────────────────────────
const QR_DOTS = [1,0,1,1,0,0,1,0,1,0,1,1,0,0,1,1,0,1,0,1,1,1,0,1,1]
export function QRBox({ label, sublabel, size = 110 }) {
  return (
    <div className="text-center">
      <div className="qr-box mb-2" style={{ width: size, height: size }}>
        <div className="qr-corner qr-corner-tl" />
        <div className="qr-corner qr-corner-tr" />
        <div className="qr-corner qr-corner-bl" />
        <div className="qr-corner qr-corner-br" />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 10px)', gap: 3 }}>
          {QR_DOTS.map((v, i) => (
            <div key={i} style={{ width: 10, height: 10, borderRadius: 2, background: v ? '#1D6FA8' : 'transparent' }} />
          ))}
        </div>
      </div>
      {label    && <p className="text-xs font-medium text-brand-700">{label}</p>}
      {sublabel && <p className="text-[11px] text-gray-400 mt-0.5">{sublabel}</p>}
    </div>
  )
}

// ── ColorSwatch ──────────────────────────────────────────
export function ColorSwatch({ color, size = 'w-3.5 h-3.5', className }) {
  return (
    <div 
      className={clsx(size, "rounded-full border border-gray-200 shadow-sm flex-shrink-0 relative overflow-hidden", className)}
      style={{ background: color || '#f3f4f6' }}
    >
      <div className="absolute inset-0 bg-gradient-to-tr from-black/10 via-transparent to-white/20" />
    </div>
  )
}

// ── VarianceAlert ────────────────────────────────────────
export function VarianceAlert({ pct, expectedKg, actualKg, children }) {
  return (
    <div className="variance-alert">
      <p className="text-4xl font-semibold text-red-700 text-center">{pct}</p>
      <p className="text-sm text-gray-500 text-center mb-4">Variance detected · threshold is 2%</p>
      {expectedKg && actualKg && (
        <div className="grid grid-cols-2 gap-2 mb-4">
          <div className="text-center bg-white rounded-lg p-3">
            <p className="text-[11px] text-red-600">Expected</p>
            <p className="text-lg font-semibold text-red-700">{expectedKg} KG</p>
          </div>
          <div className="text-center bg-white rounded-lg p-3">
            <p className="text-[11px] text-red-600">Actual</p>
            <p className="text-lg font-semibold text-red-700">{actualKg} KG</p>
          </div>
        </div>
      )}
      {children}
    </div>
  )
}

// ── SuccessScreen ────────────────────────────────────────
export function SuccessScreen({ title, subtitle, iconColor = 'green' }) {
  const bgColor  = iconColor === 'amber' ? 'bg-amber-50' : 'bg-green-50'
  const txtColor = iconColor === 'amber' ? 'text-amber-700' : 'text-green-700'
  const stroke   = iconColor === 'amber' ? '#B45309' : '#1A7F5A'
  return (
    <div className="text-center py-6">
      <div className={clsx('w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3', bgColor)}>
        {iconColor === 'amber'
          ? <svg width="28" height="28" viewBox="0 0 24 24" fill="none"><path d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" stroke={stroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
          : <svg width="28" height="28" viewBox="0 0 24 24" fill="none"><path d="M5 13l4 4L19 7" stroke={stroke} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
        }
      </div>
      <p className={clsx('text-lg font-semibold', txtColor)}>{title}</p>
      {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
    </div>
  )
}

// ── Admin Card ───────────────────────────────────────────
export function Card({ title, action, children, className, bodyClass }) {
  return (
    <div className={clsx('admin-card', className)}>
      {(title || action) && (
        <div className="admin-card-header">
          {title && <h3 className="admin-card-title">{title}</h3>}
          {action && <div>{action}</div>}
        </div>
      )}
      <div className={clsx('admin-card-body', bodyClass)}>{children}</div>
    </div>
  )
}

// ── StatCard ─────────────────────────────────────────────
const ACCENT_TOPS = { brand: 'border-t-brand-600', green: 'border-t-green-500', amber: 'border-t-amber-500', red: 'border-t-red-500' }
export function StatCard({ label, value, sub, subColor, accent = 'brand' }) {
  return (
    <div className={clsx('stat-card border-t-4', ACCENT_TOPS[accent] || ACCENT_TOPS.brand)}>
      <div className="stat-label">{label}</div>
      <div className="stat-val">{value}</div>
      {sub && <div className={clsx('stat-sub', subColor || 'text-gray-400')}>{sub}</div>}
    </div>
  )
}

// ── Form helpers ─────────────────────────────────────────
export function FormSection({ label, hint, children }) {
  return (
    <div className="mb-4">
      {label && <label className="form-label">{label}</label>}
      {children}
      {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
    </div>
  )
}
export function Input({ label, hint, className, ...props }) {
  return <FormSection label={label} hint={hint}><input className={clsx('form-input', className)} {...props} /></FormSection>
}
export function Select({ label, options = [], className, ...props }) {
  return (
    <FormSection label={label}>
      <select className={clsx('form-input', className)} {...props}>
        {options.map((o) => (
          <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>
        ))}
      </select>
    </FormSection>
  )
}
export function Textarea({ label, className, ...props }) {
  return <FormSection label={label}><textarea className={clsx('form-input', className)} {...props} /></FormSection>
}

// ── RichSelect (Radix-based premium selector) ───────────
export function RichSelect({ 
  label, 
  value, 
  onChange, 
  options = [], 
  placeholder = 'Select option...', 
  className,
  renderItem, // Optional custom item renderer
}) {
  const EMPTY_VAL = '__EMPTY__'

  const renderOptionLabel = (option) => {
    const rawLabel = option?.label
    if (typeof rawLabel === 'string' || typeof rawLabel === 'number') return rawLabel
    if (rawLabel && typeof rawLabel === 'object') {
      return rawLabel.name || rawLabel.title || rawLabel.code || 'Option'
    }
    return ''
  }
  
  // Map incoming value/options to handle empty strings
  const internalValue = (value === '' || value === null || value === undefined) ? EMPTY_VAL : String(value)
  const selected = options.find(o => {
    const ov = (o.value === '' || o.value === null || o.value === undefined) ? EMPTY_VAL : String(o.value)
    return ov === internalValue
  })

  const handleValueChange = (val) => {
    onChange(val === EMPTY_VAL ? '' : val)
  }

  return (
    <FormSection label={label}>
      <SelectPrimitive.Root value={internalValue} onValueChange={handleValueChange}>
        <SelectPrimitive.Trigger 
          className={clsx(
            'flex items-center justify-between w-full px-3 h-10 border border-gray-200 rounded-xl text-sm bg-white',
            'hover:border-gray-300 focus:outline-none focus:ring-4 focus:ring-brand-500/10 focus:border-brand-500/60 transition-all outline-none group',
            className
          )}
        >
          <div className="flex items-center gap-2 overflow-hidden">
            {selected ? (
              renderItem ? renderItem(selected) : (
                <SelectPrimitive.Value placeholder={placeholder}>
                  {renderOptionLabel(selected)}
                </SelectPrimitive.Value>
              )
            ) : (
              <span className="text-gray-400">{placeholder}</span>
            )}
          </div>
          <SelectPrimitive.Icon className="text-gray-400 group-hover:text-gray-600 transition-colors">
            <ChevronDown size={16} />
          </SelectPrimitive.Icon>
        </SelectPrimitive.Trigger>

        <SelectPrimitive.Portal>
          <SelectPrimitive.Content 
            className="overflow-hidden bg-white rounded-xl shadow-2xl border border-gray-100 z-[100] animate-in fade-in zoom-in-95 duration-150 min-w-[var(--radix-select-trigger-width)]"
            position="popper"
            sideOffset={5}
          >
            <SelectPrimitive.Viewport className="p-1.5">
              {options.map((option) => {
                const optVal = (option.value === '' || option.value === null || option.value === undefined) ? EMPTY_VAL : String(option.value)
                return (
                  <SelectPrimitive.Item 
                    key={option.value || 'empty'} 
                    value={optVal}
                    className={clsx(
                      'relative flex items-center px-8 py-2.5 text-sm rounded-lg outline-none cursor-pointer overflow-hidden',
                      'focus:bg-brand-50 focus:text-brand-700 data-[state=checked]:bg-brand-50 data-[state=checked]:text-brand-700 transition-colors'
                    )}
                  >
                    <SelectPrimitive.ItemIndicator className="absolute left-2.5 inline-flex items-center justify-center text-brand-600">
                      <Check size={14} strokeWidth={3} />
                    </SelectPrimitive.ItemIndicator>
                    
                    <SelectPrimitive.ItemText>
                      {renderItem ? renderItem(option) : renderOptionLabel(option)}
                    </SelectPrimitive.ItemText>
                  </SelectPrimitive.Item>
                )
              })}
            </SelectPrimitive.Viewport>
          </SelectPrimitive.Content>
        </SelectPrimitive.Portal>
      </SelectPrimitive.Root>
    </FormSection>
  )
}

// ── Misc ─────────────────────────────────────────────────
export function Divider({ className }) {
  return <div className={clsx('divider', className)} />
}
export function SectionHeading({ children, className }) {
  return <p className={clsx('section-heading', className)}>{children}</p>
}
export function KVRow({ label, value, valueClass, highlight, bold }) {
  return (
    <div className={clsx('kv-row px-1 py-1.5 flex justify-between items-center border-b border-gray-50 last:border-0', highlight && 'bg-brand-50/30')}>
      <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-tight">{label}</span>
      <span className={clsx('text-sm font-medium text-gray-800', valueClass, bold && 'font-bold text-brand-700')}>{value}</span>
    </div>
  )
}

// ── Modal ────────────────────────────────────────────────
export function Modal({ isOpen, onClose, title, children, size = 'md', confirmClose = false }) {
  if (!isOpen) return null

  const handleClose = () => {
    if (confirmClose) {
      if (window.confirm('You have unsaved changes. Your details will be lost. Are you sure you want to close?')) {
        onClose()
      }
    } else {
      onClose()
    }
  }

  const sizes = {
    sm: 'max-w-md',
    md: 'max-w-xl',
    lg: 'max-w-3xl',
    xl: 'max-w-5xl',
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 overflow-y-auto">
      <div 
        className="fixed inset-0 bg-gray-900/60 backdrop-blur-[6px] animate-in fade-in duration-300" 
        onClick={handleClose} 
      />
      <div className={clsx(
        'relative bg-white rounded-[24px] shadow-[0_32px_64px_-12px_rgba(0,0,0,0.14)] w-full overflow-hidden animate-in zoom-in-95 duration-300',
        sizes[size] || sizes.md
      )}>
        {title && (
          <div className="px-8 py-5 border-b border-gray-100 flex items-center justify-between bg-white/80 backdrop-blur-md sticky top-0 z-10">
            <h3 className="text-lg font-bold text-gray-900 tracking-tight">{title}</h3>
            <button onClick={handleClose} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-all">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
        )}
        {!title && (
          <button 
            onClick={handleClose} 
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-300 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-all z-20"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        )}
        <div className={clsx('max-h-[85vh] overflow-y-auto custom-scrollbar', title ? 'p-8' : 'p-10')}>
          {children}
        </div>
      </div>
    </div>
  )
}
export function ProgressBar({ pct, color = 'bg-brand-600' }) {
  return (
    <div className="progress-bar">
      <div className={clsx('progress-fill', color)} style={{ width: `${pct}%` }} />
    </div>
  )
}
export function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center py-16">
      <div className="w-8 h-8 border-2 border-brand-600 border-t-transparent rounded-full animate-spin" />
    </div>
  )
}
export function PageEmpty({ message = 'No data found' }) {
  return <div className="text-center py-16 text-gray-400 text-sm">{message}</div>
}
export function SearchBar({ value, onChange, placeholder = 'Search…', children }) {
  return (
    <div className="flex gap-2 mb-4 flex-wrap">
      <div className="relative flex-1 min-w-[200px] max-w-xs">
        <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" width="14" height="14" viewBox="0 0 16 16" fill="none">
          <circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.3"/>
          <path d="M10.5 10.5l3 3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
        </svg>
        <input className="form-input pl-8" value={value} onChange={onChange} placeholder={placeholder} />
      </div>
      {children}
    </div>
  )
}
// ── Checkbox ─────────────────────────────────────────────
export function Checkbox({ checked, onChange, disabled, className }) {
  return (
    <div 
      onClick={() => !disabled && onChange(!checked)}
      className={clsx(
        'w-4.5 h-4.5 rounded-md border-2 flex items-center justify-center transition-all cursor-pointer select-none',
        checked 
          ? 'bg-brand-600 border-brand-600 shadow-[0_2px_8px_rgba(var(--brand-600),0.3)]' 
          : 'bg-white border-gray-200 hover:border-gray-300',
        disabled && 'opacity-40 cursor-not-allowed',
        className
      )}
    >
      {checked && (
        <svg 
          width="10" height="8" viewBox="0 0 12 10" fill="none" 
          className="animate-in zoom-in-50 duration-200"
        >
          <path d="M1 5L4.5 8.5L11 1" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )}
    </div>
  )
}
// ── ConfirmDialog ────────────────────────────────────────
export function ConfirmDialog({ isOpen, onClose, onConfirm, title, message, confirmText = 'Confirm', cancelText = 'Cancel', variant = 'danger', isLoading, error }) {
  const isDanger = variant === 'danger'
  const iconBg   = isDanger ? 'bg-red-50 text-red-600' : 'bg-brand-50 text-brand-600'
  const btnClass = isDanger 
    ? 'bg-red-600 text-white hover:bg-red-700 shadow-md shadow-red-200' 
    : 'bg-brand-600 text-white hover:bg-brand-700 shadow-md shadow-brand-200'

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={null} size="sm">
      <div className="flex flex-col items-center text-center py-4 px-2">
        {/* Icon Circle */}
        <div className={clsx('w-20 h-20 rounded-full flex items-center justify-center mb-6 animate-in zoom-in-50 duration-300', iconBg)}>
          {isDanger ? (
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2M10 11v6M14 11v6"/>
            </svg>
          ) : (
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
            </svg>
          )}
        </div>

        {/* Text Content */}
        <h3 className="text-2xl font-bold text-gray-900 mb-3 tracking-tight leading-tight">{title}</h3>
        <p className="text-sm text-gray-500 leading-relaxed mb-6 max-w-[280px] mx-auto">{message}</p>
        
        {error && (
          <div className="w-full bg-red-50 border border-red-100 text-red-600 px-4 py-2.5 rounded-xl text-xs font-semibold flex items-center gap-2 mb-8 animate-in fade-in slide-in-from-top-2">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            <span className="flex-1 text-left">{error}</span>
          </div>
        )}
        
        {/* Actions Grid */}
        <div className="grid grid-cols-2 gap-4 w-full">
          <Button 
            variant="secondary" 
            onClick={onClose} 
            className="w-full h-12 justify-center border-gray-100 hover:border-gray-300 hover:bg-gray-50 text-gray-600 transition-all font-semibold"
            disabled={isLoading}
          >
            {cancelText}
          </Button>
          <button 
            onClick={onConfirm} 
            disabled={isLoading}
            className={clsx(
              'flex items-center justify-center gap-2 px-6 h-12 rounded-xl text-sm font-bold transition-all active:scale-[0.97] outline-none',
              btnClass,
              isLoading && 'opacity-70 cursor-not-allowed cursor-wait'
            )}
          >
            {isLoading ? (
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              confirmText
            )}
          </button>
        </div>
      </div>
    </Modal>
  )
}
export function Tabs({ tabs, active, onChange }) {
  return (
    <div className="no-scrollbar flex gap-0 border-b border-gray-100 mb-5 overflow-x-auto">
      {tabs.map((tab) => (
        <button
          key={tab}
          onClick={() => onChange(tab)}
          className={clsx(
            'px-4 py-2 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors',
            active === tab ? 'border-brand-600 text-brand-700' : 'border-transparent text-gray-400 hover:text-gray-600'
          )}
        >
          {tab}
        </button>
      ))}
    </div>
  )
}
